import * as Yup from 'yup';
import { TFunction } from 'react-i18next';
// import { PaymentsGatewayTypes } from '../constants/payments-gateway/payments-gateway-form';
import { Shape } from '../constants/yup_types';
import { ApplicationFieldsReturnTypes } from '../constants/new-application/new-application-form';
import { ApiKey } from '../views/developers/ApiKey';
import { TokenizationPage } from '../views/developers/TokenizationPage';
import { MerchantFeesHistoryPage } from '../views/merchants/details/MerchantFeesHistoryPage';
import { MerchantSitesPage } from '../views/merchants/details/MerchantSitesPage';
import { InitialValuesTypes, Params } from '../store/merchnts/merchant-interfaces';
import Business from '../views/merchants/details/MerchantBusinessPage';
import Address from '../views/merchants/details/MerchantAddressPage';
import Contact from '../views/merchants/details/MerchantContactPage';
import Bank from '../views/merchants/details/MerchantBankPage';
import CustomerSupport from '../views/merchants/details/MerchantCustomerSupportPage';
import Developer from '../views/merchants/details/MerchantDeveloperPage';
import SpecialTerms from '../views/merchants/details/MerchantSpecialTermsPage';
import {
	convertCustomerFieldsToInitial,
	customerInitialValues, getCustomerValidationSchema,
	getFormattedCustomerData,
	getFormattedCustomerFormFields
} from '../constants/merchant/customer-support-form';
import {
	convertToDeveloperInitial,
	getDeveloperValidationSchema, getFormattedDeveloperData, getFormattedDeveloperFormFields,
	initialDeveloperValues
} from '../constants/merchant/developer-form';
import {
	bankInitialValues,
	convertBankFieldsToInitial,
	getFormattedBankData,
	getFormattedBankFormFields
} from '../constants/merchant/bank-form';
import {
	convertToContactInitial,
	getContactValidationSchema,
	getFormattedContactData,
	getFormattedContactFormFields,
	initialContactValues
} from '../constants/merchant/contact-form';
import {
	addressInitialValues, convertAddressFieldsToInitial,
	getFormattedAddressData,
	getFormattedAddressFormFields
} from '../constants/merchant/address-form';
import {
	convertToBusinessInitial, getBusinessValidationSchema,
	getFormattedBusinessData,
	getFormattedBusinessFormFields,
	initialBusinessValues
} from '../constants/merchant/business-form';
import {
	convertConfigurationsToInitial,
	getFormattedConfigurationData,
	getFormattedConfigurations,
	initialConfigurations
} from '../constants/merchant/configurations-form';
import {
	convertAdminToGeneralInitialValues, convertPartnerToGeneralInitialValues,
	getAdminFormattedGeneralData, getAdminFormattedGeneralFormFields,
	getPartnerFormattedGeneralData, getPartnerFormattedGeneralFormFields, initialAdminGeneralValues,
	initialPartnerGeneralValues,
} from '../constants/merchant/general-form';
import {
	convertToSpecialTermsInitial,
	getFormattedSpecialTermsData,
	getFormattedSpecialTermsField,
	initialSpecialTermsValues
} from '../constants/merchant/special-terms-form';
import { MerchantFeesPage } from '../views/merchants/details/MerchantFeesPage';
import { MerchantConfigurationPage } from '../views/merchants/details/MerchantConfigurationPage';
import { IMerchantData } from '../interfaces/merchant';
import { MerchantWebhooksListPage } from '../views/merchants/details/MerchantWebhooksListPage';

export enum FORM_FIELDS {
	COMPANY_NAME = 'LegalEntityName',
	DOING_BUSINESS = 'Dba',
	BUSINESS_ESTABLISHED_DATE = 'BusinessEstablishedDate',
	PRODUCTS_SOLD = 'ProductsSold',
	TAX = 'TaxId',
	WEB_URL = 'Website',
	INDUSTRY = 'Industry',
	BUSINESS_TYPE = 'Type',
	PLATFORM = 'EcommercePlatform',
	LOGO = 'LogoFile',
	LOGO_URL = 'LogoUrl',
	MCC = 'Mcc',
	DESCRIPTOR = 'Descriptor',
	DESCRIPTION = 'Description',
	PCI_DSS = 'Pcidss',
	SITE_VERIFICATION = 'isSiteValidated',
	LINE_1 = 'AddressAddressline1',
	LINE_2 = 'AddressAddressline2',
	CITY = 'AddressCity',
	ZIPCODE = 'AddressZipCode',
	COUNTRY = 'AddressCountry',
	STATE = 'AddressState',
	FIRST_NAME = 'PrimaryContactFirstName',
	LAST_NAME = 'PrimaryContactLastName',
	EMAIL = 'PrimaryContactEmail',
	PHONE = 'PrimaryContactPhone',
	DDA_TYPE = 'BankAccountInformationDdaType',
	ACCOUNT_NUMBER = 'BankAccountInformationAccountNumber',
	MASKED_ACCOUNT_NUMBER = 'maskedAccountNumber',
	MASKED_ROUTING_NUMBER = 'maskedRoutingNumber',
	ROUTING_NUMBER = 'BankAccountInformationRoutingNumber',
	BANK_NAME = 'BankAccountInformationBankName',
	PARTNER_ID = 'PartnerId',
	SALES_AGENCY_ID = 'salesAgencyId',
	INTEGRATION_PARTNER = 'IntegrationPartnerId',
	SPECIAL_TERMS = 'SpecialTerms',
	LIMIT_INTERVAL = 'LimitInterval',
	LIMIT_COUNT = 'LimitCount',
	MAX_PER_DAY = 'MaxPerDay',
	GHOST_PERCENTAGE = 'GhostModeThrottlePercentage',
	THROTTLE_PERCENTAGE = 'ThrottlePercentage',
	DYNAMIC_AUTHORIZATION_DISCOUNT_THROTTLE_PERCENTAGE = 'dynamicAuthorizationDiscountThrottlePercentage',
	MAXIMUM_ORDERS = 'Maximum orders',
	NFS_DECLINE_THROTTLING = 'NfsDeclineThrottling',
	BUREAU_PRODUCTION = 'IsBureauProductionActive',
	LOCK_MERCHANT = 'Locked',
	PAYOUTS_ENABLE = 'PayoutsEnabled',
	MIT_ENABLE = 'isMitEnabled',
	MIT_EVALUATE_ASYNC = 'isMITEvaluateAsync',
	CIT_EVALUATE_ASYNC = 'isCITEvaluateAsync',
	CUSTOMER_NAME = 'CustomerSupportName',
	SUPPORT_LINK = 'CustomerSupportLink',
	CUSTOMER_EMAIL = 'CustomerSupportEmail',
	CUSTOMER_PHONE = 'CustomerSupportPhone',
	DEVELOPER_FIRST_NAME = 'DeveloperFirstName',
	DEVELOPER_LAST_NAME = 'DeveloperLastName',
	DEVELOPER_EMAIL = 'DeveloperEmail',
	DEVELOPER_PHONE = 'DeveloperPhone',
	VIRTUAL_TERMINAL_ENABLED = 'virtualTerminalEnabled',
	MIT_GET_SITE_BY_DYNAMIC_DESCRIPTION_ENABLED = 'mitGetSiteByDynamicDescriptorEnabled',
	MIT_CONSUMER_NOTIFICATIONS_ENABLED = 'mitConsumerNotificationsEnabled',
	CONSUMER_NOTIFICATION_CHANNEL = 'consumerNotificationChannel',
	MIT_CONSUMER_CURES_ENABLED = 'mitConsumerCuresEnabled',
	UI_WIDGET_OPTIONAL = 'uiWidgetOptional',
	CIT_CONSUMER_NOTIFICATIONS_ENABLED = 'citConsumerNotificationsEnabled',
	ACCOUNT_ID = 'accountId',
	RISK_LEVEL = 'riskLevel',
	RISK_LEVEL_VISA = 'riskLevel_Visa',
	URLS = 'urls',
}

export enum FORM_FIELDS_NAMES {
	COMPANY_NAME = 'NEW_APPLICATION.LABEL.COMPANY_NAME',
	BUSINESS_ESTABLISHED_DATE = 'NEW_APPLICATION.LABEL.BUSINESS_ESTABLISHED_DATE',
	DOING_BUSINESS = 'NEW_APPLICATION.LABEL.DOING_BUSINESS',
	PRODUCTS_SOLD = 'NEW_APPLICATION.LABEL.PRODUCTS_SOLD',
	TAX = 'NEW_APPLICATION.LABEL.TAX',
	WEB_URL = 'NEW_APPLICATION.LABEL.WEB_URL',
	INDUSTRY = 'NEW_APPLICATION.LABEL.INDUSTRY',
	BUSINESS_TYPE = 'NEW_APPLICATION.LABEL.BUSINESS_TYPE',
	PLATFORM = 'NEW_APPLICATION.LABEL.PLATFORM',
	LOGO = 'NEW_APPLICATION.LABEL.LOGO',
	MCC = 'NEW_APPLICATION.LABEL.MCC',
	DESCRIPTION = 'NEW_APPLICATION.LABEL.DESCRIPTION',
	DESCRIPTOR = 'NEW_APPLICATION.LABEL.DESCRIPTOR',
	PCI_DSS = 'NEW_APPLICATION.LABEL.PCI_DSS',
	SITE_VERIFICATION = 'NEW_APPLICATION.LABEL.SITE_VERIFICATION',
	LINE_1 = 'NEW_APPLICATION.LABEL.LINE_1',
	LINE_2 = 'NEW_APPLICATION.LABEL.LINE_2',
	CITY = 'NEW_APPLICATION.LABEL.CITY',
	ZIPCODE = 'NEW_APPLICATION.LABEL.ZIPCODE',
	COUNTRY = 'NEW_APPLICATION.LABEL.COUNTRY',
	STATE = 'NEW_APPLICATION.LABEL.STATE',
	FIRST_NAME = 'NEW_APPLICATION.LABEL.FIRST_NAME',
	LAST_NAME = 'NEW_APPLICATION.LABEL.LAST_NAME',
	EMAIL = 'NEW_APPLICATION.LABEL.EMAIL',
	PHONE = 'NEW_APPLICATION.LABEL.PHONE',
	DDA_TYPE = 'NEW_APPLICATION.LABEL.DDA_TYPE',
	ACCOUNT_NUMBER = 'NEW_APPLICATION.LABEL.ACCOUNT_NUMBER',
	ROUTING_NUMBER = 'NEW_APPLICATION.LABEL.ROUTING_NUMBER',
	BANK_NAME = 'NEW_APPLICATION.LABEL.BANK_NAME',
	PARTNER_ID = 'NEW_APPLICATION.LABEL.PARTNER_ID',
	PARTNER = 'NEW_APPLICATION.LABEL.PARTNER',
	SPECIAL_TERMS = 'NEW_APPLICATION.LABEL.SPECIAL_TERMS',
	BUREAU_PRODUCTION = 'NEW_APPLICATION.LABEL.BUREAU_PRODUCTION',
	LOCK_MERCHANT = 'NEW_APPLICATION.LABEL.LOCK_MERCHANT',
	PAYOUTS_ENABLE = 'NEW_APPLICATION.LABEL.PAYOUTS_ENABLE',
	MIT_ENABLE = 'NEW_APPLICATION.LABEL.MIT_ENABLE',
	EVALUATE_ASYNC = 'NEW_APPLICATION.LABEL.EVALUATE_ASYNC',
	CUSTOMER_NAME = 'NEW_APPLICATION.LABEL.CUSTOMER_NAME',
	SUPPORT_LINK = 'NEW_APPLICATION.LABEL.SUPPORT_LINK',
	CUSTOMER_EMAIL = 'NEW_APPLICATION.LABEL.CUSTOMER_EMAIL',
	CUSTOMER_PHONE = 'NEW_APPLICATION.LABEL.CUSTOMER_PHONE',
	DEVELOPER_FIRST_NAME = 'NEW_APPLICATION.LABEL.FIRST_NAME',
	DEVELOPER_LAST_NAME = 'NEW_APPLICATION.LABEL.LAST_NAME',
	DEVELOPER_EMAIL = 'NEW_APPLICATION.LABEL.EMAIL',
	DEVELOPER_PHONE = 'NEW_APPLICATION.LABEL.PHONE',
	VIRTUAL_TERMINAL_ENABLED = 'NEW_APPLICATION.LABEL.VIRTUAL_TERMINAL_ENABLED',
	MIT_GET_SITE_BY_DYNAMIC_DESCRIPTION_ENABLED = 'NEW_APPLICATION.LABEL.MIT_GET_SITE_BY_DYNAMIC_DESCRIPTION_ENABLED',
	MIT_CONSUMER_NOTIFICATIONS_ENABLED = 'NEW_APPLICATION.LABEL.MIT_CONSUMER_NOTIFICATIONS_ENABLED',
	MIT_CONSUMER_CURES_ENABLED = 'NEW_APPLICATION.LABEL.MIT_CONSUMER_CURES_ENABLED',
	UI_WIDGET_OPTIONAL = 'NEW_APPLICATION.LABEL.UI_WIDGET_OPTIONAL',
	CIT_CONSUMER_NOTIFICATIONS_ENABLED = 'NEW_APPLICATION.LABEL.CIT_CONSUMER_NOTIFICATIONS_ENABLED',
	ACCOUNT_ID = 'Account',
}

// 500kb in bytes
export const FILE_SIZE = 500 * 1000;
export const SUPPORTED_FORMATS = ['image/jpeg', 'image/png', 'image/jpg'];

// @ts-ignore
export const getMerchantDetailsSchema = (t: TFunction): Yup.SchemaOf<IMerchantDetails> => Yup.object<Shape<IMerchantDetails>>().shape({
	[FORM_FIELDS.COMPANY_NAME]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.COMPANY_NAME_REQUIRED'))
		.max(100, t('NEW_APPLICATION.VALIDATION.COMPANY_NAME_LENGTH')),
	// [FORM_FIELDS.DOING_BUSINESS]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.DOING_BUSINESS_REQUIRED'))
	// 	.max(100, t('NEW_APPLICATION.VALIDATION.DOING_BUSINESS_LENGTH')),
	// [FORM_FIELDS.TAX]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.TAX_REQUIRED')),
	// [FORM_FIELDS.WEB_URL]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.WEB_URL_REQUIRED'))
	// 	.url(t('NEW_APPLICATION.VALIDATION.WEB_URL_FORMAT')),
	// [FORM_FIELDS.INDUSTRY]: Yup.string()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.INDUSTRY_REQUIRED'))
	// 	.required(t('NEW_APPLICATION.VALIDATION.INDUSTRY_REQUIRED')),
	// [FORM_FIELDS.BUSINESS_TYPE]: Yup.string()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.BUSINESS_TYPE_REQUIRED'))
	// 	.required(t('NEW_APPLICATION.VALIDATION.BUSINESS_TYPE_REQUIRED')),

	// [FORM_FIELDS.MCC]: Yup.string()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.MCC_REQUIRED'))
	// 	.required(t('NEW_APPLICATION.VALIDATION.MCC_REQUIRED')),
	// [FORM_FIELDS.PCI_DSS]: Yup.boolean(),
	// [FORM_FIELDS.LINE_1]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.LINE_1_REQUIRED')),
	// [FORM_FIELDS.CITY]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.CITY_REQUIRED')),
	// [FORM_FIELDS.ZIPCODE]: Yup.number()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
	// 	.required(t('NEW_APPLICATION.VALIDATION.ZIPCODE_REQUIRED')),
	// [FORM_FIELDS.COUNTRY]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.COUNTRY_REQUIRED')),
	// [FORM_FIELDS.STATE]: Yup.string()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.STATE_REQUIRED'))
	// 	.required(t('NEW_APPLICATION.VALIDATION.STATE_REQUIRED')),

	[FORM_FIELDS.FIRST_NAME]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.FIRST_NAME_REQUIRED')),
	[FORM_FIELDS.LAST_NAME]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.LAST_NAME_REQUIRED')),
	[FORM_FIELDS.EMAIL]: Yup.string()
		.email(t('NEW_APPLICATION.VALIDATION.EMAIL_ERROR_TEXT'))
		.required(t('NEW_APPLICATION.VALIDATION.EMAIL_REQUIRED')),
	[FORM_FIELDS.PHONE]: Yup.number()
		.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
		.required(t('NEW_APPLICATION.VALIDATION.PHONE_REQUIRED')),
	[FORM_FIELDS.DESCRIPTOR]: Yup.string()
		.required(t('NEW_APPLICATION.VALIDATION.DESCRIPTOR_REQUIRED')),

	// [FORM_FIELDS.DDA_TYPE]: Yup.string()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.DDA_TYPE_REQUIRED'))
	// 	.required(t('NEW_APPLICATION.VALIDATION.DDA_TYPE_REQUIRED')),
	// [FORM_FIELDS.BANK_NAME]: Yup.string()
	// 	.required(t('NEW_APPLICATION.VALIDATION.BANK_NAME_REQUIRED'))
	// 	.max(50, t('NEW_APPLICATION.VALIDATION.BANK_NAME_LENGTH')),
	// [FORM_FIELDS.LIMIT_INTERVAL]: Yup.number()
	// 	.nullable()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.LIMIT_COUNT]: Yup.number()
	// 	.nullable()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.MAX_PER_DAY]: Yup.number()
	// 	.nullable()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.THROTTLE_PERCENTAGE]: Yup.number()
	// 	.nullable()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.MAXIMUM_ORDERS]: Yup.number()
	// 	.nullable()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.NFS_DECLINE_THROTTLING]: Yup.number()
	// 	.nullable()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.SUPPORT_LINK]: Yup.string()
	// 	.url(t('NEW_APPLICATION.VALIDATION.SUPPORT_LINK_FORMAT')),
	// [FORM_FIELDS.CUSTOMER_EMAIL]: Yup.string()
	// 	.email(t('NEW_APPLICATION.VALIDATION.EMAIL_ERROR_TEXT')),
	// [FORM_FIELDS.CUSTOMER_PHONE]: Yup.number()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT')),
	// [FORM_FIELDS.DEVELOPER_EMAIL]: Yup.string()
	// 	.email(t('NEW_APPLICATION.VALIDATION.EMAIL_ERROR_TEXT')),
	// [FORM_FIELDS.DEVELOPER_PHONE]: Yup.number()
	// 	.typeError(t('NEW_APPLICATION.VALIDATION.NUMBER_ERROR_TEXT'))
});

export type IMerchantDetails = {
	[key in FORM_FIELDS]: number | string | string[] | Date | boolean | { [K in any] : never };
};

// export const initialValues: IMerchantDetails = {
// 	[FORM_FIELDS.COMPANY_NAME]: '',
// 	[FORM_FIELDS.DOING_BUSINESS]: '',
// 	[FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]: null,
// 	[FORM_FIELDS.TAX]: '',
// 	[FORM_FIELDS.WEB_URL]: '',
// 	[FORM_FIELDS.INDUSTRY]: '',
// 	[FORM_FIELDS.BUSINESS_TYPE]: '',
// 	[FORM_FIELDS.PRODUCTS_SOLD]: '',
// 	[FORM_FIELDS.PLATFORM]: '',
// 	[FORM_FIELDS.LOGO]: '',
// 	[FORM_FIELDS.LOGO_URL]: '',
// 	[FORM_FIELDS.MCC]: '',
// 	[FORM_FIELDS.DESCRIPTION]: '',
// 	[FORM_FIELDS.DESCRIPTOR]: '',
// 	[FORM_FIELDS.PCI_DSS]: false,
// 	[FORM_FIELDS.SITE_VERIFICATION]: false,
// 	[FORM_FIELDS.LINE_1]: '',
// 	[FORM_FIELDS.LINE_2]: '',
// 	[FORM_FIELDS.CITY]: '',
// 	[FORM_FIELDS.ZIPCODE]: '',
// 	[FORM_FIELDS.COUNTRY]: 'USA',
// 	[FORM_FIELDS.STATE]: '',
// 	[FORM_FIELDS.FIRST_NAME]: '',
// 	[FORM_FIELDS.LAST_NAME]: '',
// 	[FORM_FIELDS.EMAIL]: '',
// 	[FORM_FIELDS.PHONE]: '',
// 	[FORM_FIELDS.DDA_TYPE]: '',
// 	[FORM_FIELDS.ACCOUNT_NUMBER]: '',
// 	[FORM_FIELDS.ROUTING_NUMBER]: '',
// 	[FORM_FIELDS.MASKED_ACCOUNT_NUMBER]: null,
// 	[FORM_FIELDS.MASKED_ROUTING_NUMBER]: null,
// 	[FORM_FIELDS.BANK_NAME]: '',
// 	[FORM_FIELDS.PARTNER_ID]: '',
// 	[FORM_FIELDS.SALES_AGENCY_ID]: '',
// 	// [FORM_FIELDS.PARTNER]: {},
// 	[FORM_FIELDS.SPECIAL_TERMS]: '',
// 	[FORM_FIELDS.LIMIT_INTERVAL]: '',
// 	[FORM_FIELDS.LIMIT_COUNT]: '',
// 	[FORM_FIELDS.MAX_PER_DAY]: '',
// 	[FORM_FIELDS.GHOST_PERCENTAGE]: 100,
// 	[FORM_FIELDS.THROTTLE_PERCENTAGE]: '',
// 	[FORM_FIELDS.DYNAMIC_AUTHORIZATION_DISCOUNT_THROTTLE_PERCENTAGE]: '',
// 	[FORM_FIELDS.MAXIMUM_ORDERS]: '',
// 	[FORM_FIELDS.NFS_DECLINE_THROTTLING]: '',
// 	[FORM_FIELDS.BUREAU_PRODUCTION]: false,
// 	[FORM_FIELDS.LOCK_MERCHANT]: false,
// 	[FORM_FIELDS.PAYOUTS_ENABLE]: false,
// 	[FORM_FIELDS.MIT_ENABLE]: false,
// 	[FORM_FIELDS.EVALUATE_ASYNC]: false,
// 	[FORM_FIELDS.CUSTOMER_NAME]: '',
// 	[FORM_FIELDS.SUPPORT_LINK]: '',
// 	[FORM_FIELDS.CUSTOMER_EMAIL]: '',
// 	[FORM_FIELDS.CUSTOMER_PHONE]: '',
// 	[FORM_FIELDS.DEVELOPER_FIRST_NAME]: '',
// 	[FORM_FIELDS.DEVELOPER_LAST_NAME]: '',
// 	[FORM_FIELDS.DEVELOPER_EMAIL]: '',
// 	[FORM_FIELDS.DEVELOPER_PHONE]: '',
// 	[FORM_FIELDS.VIRTUAL_TERMINAL_ENABLED]: false,
// 	[FORM_FIELDS.MIT_GET_SITE_BY_DYNAMIC_DESCRIPTION_ENABLED]: false,
// 	[FORM_FIELDS.MIT_CONSUMER_NOTIFICATIONS_ENABLED]: false,
// 	[FORM_FIELDS.MIT_CONSUMER_CURES_ENABLED]: false,
// 	[FORM_FIELDS.UI_WIDGET_OPTIONAL]: false,
// 	[FORM_FIELDS.CIT_CONSUMER_NOTIFICATIONS_ENABLED]: false,
// 	[FORM_FIELDS.ACCOUNT_ID]: '',
// 	[FORM_FIELDS.RISK_LEVEL]: null,
// };
//
// export const getFormattedFormFields = (formikValues): IMerchantReturnTypes => ({
// 	logoFile: formikValues?.[FORM_FIELDS.LOGO],
// 	legalEntityName: formikValues?.[FORM_FIELDS.COMPANY_NAME],
// 	companyName: formikValues?.[FORM_FIELDS.COMPANY_NAME],
// 	dba: formikValues?.[FORM_FIELDS.DOING_BUSINESS],
// 	productsSold: formikValues?.[FORM_FIELDS.PRODUCTS_SOLD],
// 	type: formikValues?.[FORM_FIELDS.BUSINESS_TYPE] ? formikValues?.[FORM_FIELDS.BUSINESS_TYPE] : '',
// 	taxId: formikValues?.[FORM_FIELDS.TAX],
// 	description: formikValues?.[FORM_FIELDS.DESCRIPTION],
// 	descriptor: formikValues?.[FORM_FIELDS.DESCRIPTOR],
// 	businessEstablishedDate: formikValues?.[FORM_FIELDS.BUSINESS_ESTABLISHED_DATE] ? formikValues?.[FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]?.toISOString() : null,
// 	ecommercePlatform: formikValues?.[FORM_FIELDS.PLATFORM] ? formikValues?.[FORM_FIELDS.PLATFORM] : '',
// 	mcc: formikValues?.[FORM_FIELDS.MCC] ? formikValues?.[FORM_FIELDS.MCC] : '',
// 	industry: formikValues?.[FORM_FIELDS.INDUSTRY] || 'ANY',
// 	website: formikValues?.[FORM_FIELDS.WEB_URL],
// 	pcidss: formikValues?.[FORM_FIELDS.PCI_DSS],
// 	partnerId: formikValues?.[FORM_FIELDS.PARTNER_ID],
// 	// salesAgentId: formikValues?.[FORM_FIELDS.SALES_AGENCY_ID],
// 	isSiteValidated: formikValues?.[FORM_FIELDS.SITE_VERIFICATION],
// 	specialTerms: formikValues?.[FORM_FIELDS.SPECIAL_TERMS],
// 	accountId: formikValues?.[FORM_FIELDS.ACCOUNT_ID],
// 	address: {
// 		addressline1: formikValues?.[FORM_FIELDS.LINE_1] ? formikValues?.[FORM_FIELDS.LINE_1] : null,
// 		addressline2: formikValues?.[FORM_FIELDS.LINE_2],
// 		zipCode: formikValues?.[FORM_FIELDS.ZIPCODE].toString(),
// 		state: isUSCountry(formikValues?.[ADDRESS_FORM_FIELDS.COUNTRY]) ? formikValues?.[ADDRESS_FORM_FIELDS.STATE] : null,
// 		stateCode: null,
// 		city: formikValues?.[FORM_FIELDS.CITY],
// 		country: formikValues?.[FORM_FIELDS.COUNTRY],
// 	},
// 	primaryContact: {
// 		firstName: formikValues?.[FORM_FIELDS.FIRST_NAME],
// 		lastName: formikValues?.[FORM_FIELDS.LAST_NAME],
// 		email: formikValues?.[FORM_FIELDS.EMAIL],
// 		phone: formikValues?.[FORM_FIELDS.PHONE]
// 	},
// 	developerContact: {
// 		developerFirstName: formikValues?.[FORM_FIELDS.DEVELOPER_FIRST_NAME] ? formikValues?.[FORM_FIELDS.DEVELOPER_FIRST_NAME] : null,
// 		developerLastName: formikValues?.[FORM_FIELDS.DEVELOPER_LAST_NAME] ? formikValues?.[FORM_FIELDS.DEVELOPER_LAST_NAME] : null,
// 		developerEmail: formikValues?.[FORM_FIELDS.DEVELOPER_EMAIL] ? formikValues?.[FORM_FIELDS.DEVELOPER_EMAIL] : null,
// 		developerPhone: formikValues?.[FORM_FIELDS.DEVELOPER_PHONE] ? formikValues?.[FORM_FIELDS.DEVELOPER_PHONE] : null
// 	},
// 	bankAccountInformation: {
// 		ddaType: formikValues?.[FORM_FIELDS.DDA_TYPE] ? formikValues?.[FORM_FIELDS.DDA_TYPE] : '',
// 		accountNumber: formikValues?.[FORM_FIELDS.ACCOUNT_NUMBER],
// 		routingNumber: formikValues?.[FORM_FIELDS.ROUTING_NUMBER],
// 		maskedAccountNumber: formikValues?.[FORM_FIELDS.MASKED_ACCOUNT_NUMBER],
// 		maskedRoutingNumber: formikValues?.[FORM_FIELDS.MASKED_ROUTING_NUMBER],
// 		bankName: formikValues?.[FORM_FIELDS.BANK_NAME]
// 	},
// 	offerRequestsRateLimitIntervalMS: formikValues?.[FORM_FIELDS.LIMIT_INTERVAL],
// 	offerRequestsRateLimitCount: formikValues?.[FORM_FIELDS.LIMIT_COUNT],
// 	offerRequestsMaxPerDay: formikValues?.[FORM_FIELDS.MAX_PER_DAY],
// 	ghostModeThrottlePercentage : formikValues?.[FORM_FIELDS.GHOST_PERCENTAGE] && (formikValues?.[FORM_FIELDS.GHOST_PERCENTAGE] / 100),
// 	offerRequestsThrottlePercentage: formikValues?.[FORM_FIELDS.THROTTLE_PERCENTAGE] && (formikValues?.[FORM_FIELDS.THROTTLE_PERCENTAGE] / 100),
// 	dynamicAuthorizationDiscountThrottlePercentage: formikValues?.[FORM_FIELDS.DYNAMIC_AUTHORIZATION_DISCOUNT_THROTTLE_PERCENTAGE] && (formikValues?.[FORM_FIELDS.DYNAMIC_AUTHORIZATION_DISCOUNT_THROTTLE_PERCENTAGE] / 100),
// 	orders_MaxMonthlyAmount: formikValues?.[FORM_FIELDS.MAXIMUM_ORDERS],
// 	offer_NSF_RequestsThrottle_Percentage: formikValues?.[FORM_FIELDS.NFS_DECLINE_THROTTLING] && (formikValues?.[FORM_FIELDS.NFS_DECLINE_THROTTLING] / 100),
// 	isBureauProductionActive: formikValues?.[FORM_FIELDS.BUREAU_PRODUCTION],
// 	payoutsEnabled: formikValues?.[FORM_FIELDS.PAYOUTS_ENABLE],
// 	isMitEnabled: formikValues?.[FORM_FIELDS.MIT_ENABLE],
// 	isEvaluateAsync: formikValues?.[FORM_FIELDS.EVALUATE_ASYNC],
// 	customerSupportInformation: {
// 		customerSupportName: formikValues?.[FORM_FIELDS.CUSTOMER_NAME],
// 		customerSupportLink: formikValues?.[FORM_FIELDS.SUPPORT_LINK],
// 		customerSupportEmail: formikValues?.[FORM_FIELDS.CUSTOMER_EMAIL],
// 		customerSupportPhone: formikValues?.[FORM_FIELDS.CUSTOMER_PHONE]
// 	},
// 	virtualTerminalEnabled: formikValues?.[FORM_FIELDS.VIRTUAL_TERMINAL_ENABLED],
// 	mitGetSiteByDynamicDescriptorEnabled: formikValues?.[FORM_FIELDS.MIT_GET_SITE_BY_DYNAMIC_DESCRIPTION_ENABLED],
// 	mitConsumerNotificationsEnabled: formikValues?.[FORM_FIELDS.MIT_CONSUMER_NOTIFICATIONS_ENABLED],
// 	mitConsumerCuresEnabled: formikValues?.[FORM_FIELDS.MIT_CONSUMER_CURES_ENABLED],
// 	uiWidgetOptional: formikValues?.[FORM_FIELDS.UI_WIDGET_OPTIONAL],
// 	citConsumerNotificationsEnabled: formikValues?.[FORM_FIELDS.CIT_CONSUMER_NOTIFICATIONS_ENABLED],
// 	riskLevel: formikValues?.[FORM_FIELDS.RISK_LEVEL],
// });

export const convertToInitial = (data): IMerchantDetails => ({
	[FORM_FIELDS.COMPANY_NAME]: data?.legalEntityName,
	[FORM_FIELDS.DOING_BUSINESS]: data?.dba,
	[FORM_FIELDS.BUSINESS_ESTABLISHED_DATE]: data?.businessEstablishedDate && new Date(data?.businessEstablishedDate),
	[FORM_FIELDS.TAX]: data?.taxId,
	[FORM_FIELDS.WEB_URL]: data?.website,
	[FORM_FIELDS.INDUSTRY]: data?.industry,
	[FORM_FIELDS.BUSINESS_TYPE]: data?.type,
	[FORM_FIELDS.PRODUCTS_SOLD]: data?.productsSold,
	[FORM_FIELDS.PLATFORM]: data?.ecommercePlatform,
	[FORM_FIELDS.LOGO]: '',
	[FORM_FIELDS.LOGO_URL]: data?.logoUrl,
	[FORM_FIELDS.MCC]: data?.mcc,
	[FORM_FIELDS.PARTNER_ID]: data?.partner?.id,
	[FORM_FIELDS.INTEGRATION_PARTNER]: data?.integrationPartner?.id,
	[FORM_FIELDS.SALES_AGENCY_ID]: data?.salesAgency?.id,
	[FORM_FIELDS.DESCRIPTION]: data?.description,
	[FORM_FIELDS.DESCRIPTOR]: data?.descriptor,
	[FORM_FIELDS.PCI_DSS]: data?.pcidss,
	[FORM_FIELDS.SITE_VERIFICATION]: data?.isSiteValidated,
	[FORM_FIELDS.LINE_1]: data?.address ? data?.address?.addressline1 : '',
	[FORM_FIELDS.LINE_2]: data?.address ? data?.address?.addressline2 : '',
	[FORM_FIELDS.CITY]: data?.address ? data?.address?.city : '',
	[FORM_FIELDS.ZIPCODE]: data?.address ? data?.address?.zipCode : '',
	[FORM_FIELDS.COUNTRY]: data?.address ? data?.address?.country : '',
	[FORM_FIELDS.STATE]: data?.address ? data?.address?.state : '',
	[FORM_FIELDS.FIRST_NAME]: data?.primaryContact ? data?.primaryContact.firstName : '',
	[FORM_FIELDS.LAST_NAME]: data?.primaryContact ? data?.primaryContact.lastName : '',
	[FORM_FIELDS.EMAIL]: data?.primaryContact ? data?.primaryContact.email : '',
	[FORM_FIELDS.PHONE]: data?.primaryContact ? data?.primaryContact?.phone : '',
	[FORM_FIELDS.DDA_TYPE]: data?.bankAccountInformation ? data?.bankAccountInformation?.ddaType : '',
	[FORM_FIELDS.ACCOUNT_NUMBER]: data?.bankAccountInformation ? data?.bankAccountInformation?.accountNumber : '',
	[FORM_FIELDS.MASKED_ACCOUNT_NUMBER]: data?.bankAccountInformation ? data?.bankAccountInformation?.maskedAccountNumber : '',
	[FORM_FIELDS.MASKED_ROUTING_NUMBER]: data?.bankAccountInformation ? data?.bankAccountInformation?.maskedRoutingNumber : '',
	[FORM_FIELDS.ROUTING_NUMBER]: data?.bankAccountInformation ? data?.bankAccountInformation?.routingNumber : '',
	[FORM_FIELDS.BANK_NAME]: data?.bankAccountInformation ? data?.bankAccountInformation?.bankName : '',
	[FORM_FIELDS.SPECIAL_TERMS]: data?.specialTerms ? data?.specialTerms : '',
	[FORM_FIELDS.LIMIT_INTERVAL]: data?.offerRequestsRateLimitIntervalMS ? data.offerRequestsRateLimitIntervalMS : 0,
	[FORM_FIELDS.LIMIT_COUNT]: data?.offerRequestsRateLimitCount ? data.offerRequestsRateLimitCount : 0,
	[FORM_FIELDS.MAX_PER_DAY]: data?.offerRequestsMaxPerDay ? data.offerRequestsMaxPerDay : 0,
	[FORM_FIELDS.GHOST_PERCENTAGE]: typeof data?.ghostModeThrottlePercentage === 'number' ? (data?.ghostModeThrottlePercentage * 100) : 100,
	[FORM_FIELDS.THROTTLE_PERCENTAGE]: data?.offerRequestsThrottlePercentage ? (data?.offerRequestsThrottlePercentage * 100) : 0,
	[FORM_FIELDS.DYNAMIC_AUTHORIZATION_DISCOUNT_THROTTLE_PERCENTAGE]: data?.dynamicAuthorizationDiscountThrottlePercentage ? (data?.dynamicAuthorizationDiscountThrottlePercentage * 100) : 0,
	[FORM_FIELDS.MAXIMUM_ORDERS]: typeof data?.orders_MaxMonthlyAmount === 'number' ? data.orders_MaxMonthlyAmount : undefined,
	[FORM_FIELDS.NFS_DECLINE_THROTTLING]: typeof data?.offer_NSF_RequestsThrottle_Percentage === 'number' ? (data.offer_NSF_RequestsThrottle_Percentage * 100) : undefined,
	[FORM_FIELDS.BUREAU_PRODUCTION]: data?.isBureauProductionActive,
	[FORM_FIELDS.LOCK_MERCHANT]: data?.locked,
	[FORM_FIELDS.PAYOUTS_ENABLE]: data?.payoutsEnabled,
	[FORM_FIELDS.MIT_ENABLE]: data?.isMitEnabled,
	[FORM_FIELDS.MIT_EVALUATE_ASYNC]: data?.isMITEvaluateAsync,
	[FORM_FIELDS.CIT_EVALUATE_ASYNC]: data?.isCITEvaluateAsync,
	[FORM_FIELDS.CUSTOMER_NAME]: data?.customerSupportInformation?.customerSupportName ? data?.customerSupportInformation?.customerSupportName : '',
	[FORM_FIELDS.SUPPORT_LINK]: data?.customerSupportInformation?.customerSupportLink ? data?.customerSupportInformation?.customerSupportLink : '',
	[FORM_FIELDS.CUSTOMER_EMAIL]: data?.customerSupportInformation?.customerSupportEmail ? data?.customerSupportInformation?.customerSupportEmail : '',
	[FORM_FIELDS.CUSTOMER_PHONE]: data?.customerSupportInformation?.customerSupportPhone ? data?.customerSupportInformation?.customerSupportPhone : '',
	[FORM_FIELDS.DEVELOPER_FIRST_NAME]: data?.developerContact?.developerFirstName ? data?.developerContact?.developerFirstName : '',
	[FORM_FIELDS.DEVELOPER_LAST_NAME]: data?.developerContact?.developerLastName ? data?.developerContact?.developerLastName : '',
	[FORM_FIELDS.DEVELOPER_EMAIL]: data?.developerContact?.developerEmail ? data?.developerContact?.developerEmail : '',
	[FORM_FIELDS.DEVELOPER_PHONE]: data?.developerContact?.developerPhone ? data?.developerContact?.developerPhone : '',
	[FORM_FIELDS.VIRTUAL_TERMINAL_ENABLED]: data?.virtualTerminalEnabled,
	[FORM_FIELDS.MIT_GET_SITE_BY_DYNAMIC_DESCRIPTION_ENABLED]: data?.mitGetSiteByDynamicDescriptorEnabled,
	[FORM_FIELDS.MIT_CONSUMER_NOTIFICATIONS_ENABLED]: data?.mitConsumerNotificationsEnabled,
	[FORM_FIELDS.CONSUMER_NOTIFICATION_CHANNEL]: data?.consumerNotificationChannel,
	[FORM_FIELDS.MIT_CONSUMER_CURES_ENABLED]: data?.mitConsumerCuresEnabled,
	[FORM_FIELDS.UI_WIDGET_OPTIONAL]: data?.uiWidgetOptional,
	[FORM_FIELDS.CIT_CONSUMER_NOTIFICATIONS_ENABLED]: data?.citConsumerNotificationsEnabled,
	[FORM_FIELDS.ACCOUNT_ID]: data?.accountId,
	[FORM_FIELDS.RISK_LEVEL]: data?.riskLevel,
	[FORM_FIELDS.RISK_LEVEL_VISA]: data?.riskLevel_Visa,
	[FORM_FIELDS.URLS]: data?.sites?.map(site => site.url),
});

interface IMerchantReturnTypes extends ApplicationFieldsReturnTypes {
	offerRequestsRateLimitIntervalMS: number,
	offerRequestsRateLimitCount: number,
	offerRequestsMaxPerDay: number,
	ghostModeThrottlePercentage : number,
	offerRequestsThrottlePercentage: number,
	orders_MaxMonthlyAmount: number,
	offer_NSF_RequestsThrottle_Percentage: number,
	dynamicAuthorizationDiscountThrottlePercentage: number,
	isBureauProductionActive: boolean,
	payoutsEnabled: boolean,
	isMitEnabled: boolean,
	isMITEvaluateAsync: boolean,
	isCITEvaluateAsync: boolean,
	virtualTerminalEnabled: boolean,
	mitGetSiteByDynamicDescriptorEnabled: boolean,
	mitConsumerNotificationsEnabled: boolean,
	mitConsumerCuresEnabled: boolean,
	uiWidgetOptional: boolean | null,
	citConsumerNotificationsEnabled: boolean,
	riskLevel: number | null,
	riskLevel_Visa: number | null,
}

export enum MERCHANTS_TABS {
	BASE = 'base',
	BUSINESS = 'business-information',
	ADDRESS = 'address',
	CONTACT = 'contact-information',
	CUSTOMER = 'customer-support',
	DEVELOPER = 'developer-details',
	BANK = 'bank',
	FEES = 'fees',
	FUNDS_RESERVE = 'fund-reserve',
	SPECIAL_TERMS = 'special-terms',
	CONFIGURATIONS = 'configurations',
	SITES = 'sites',
	GENERAL = 'general',
	FEES_HISTORY = 'fees-history',
	WIDGET_CONFIGURATIONS = 'widgetConfig',
	CHECKOUT_CONFIGURATIONS = 'checkout',
	SENSE_JS = 'sense-js',
	PAYMENT_GATEWAY = 'payment-gateways',
	API_KEY = 'api-key',
	TOKENIZATION = 'tokenization',
	WEBHOOKS = 'webhooks',
	RISK_LEVEL = 'riskLevel',
	RISK_LEVEL_VISA = 'riskLevel_Visa',
	OWNER = 'owners',
	WORKFLOWS = 'risk-rules',
	DOCUMENTS = 'documents',
}

export const routes = [
	{
		key: MERCHANTS_TABS.BUSINESS,
		pathname: MERCHANTS_TABS.BUSINESS,
		component: <Business />
	},
	{
		key: MERCHANTS_TABS.ADDRESS,
		pathname: MERCHANTS_TABS.ADDRESS,
		component: <Address />
	},
	{
		key: MERCHANTS_TABS.CONTACT,
		pathname: MERCHANTS_TABS.CONTACT,
		component: <Contact />
	},
	{
		key: MERCHANTS_TABS.CUSTOMER,
		pathname: MERCHANTS_TABS.CUSTOMER,
		component: <CustomerSupport />
	},
	{
		key: MERCHANTS_TABS.DEVELOPER,
		pathname: MERCHANTS_TABS.DEVELOPER,
		component: <Developer />
	},
	{
		key: MERCHANTS_TABS.BANK,
		pathname: MERCHANTS_TABS.BANK,
		component: <Bank />
	},
	{
		key: MERCHANTS_TABS.FEES,
		pathname: MERCHANTS_TABS.FEES,
		component: <MerchantFeesPage />
	},
	{
		key: MERCHANTS_TABS.SITES,
		pathname: MERCHANTS_TABS.SITES,
		component: <MerchantSitesPage />
	},
	{
		key: MERCHANTS_TABS.SPECIAL_TERMS,
		pathname: MERCHANTS_TABS.SPECIAL_TERMS,
		component: <SpecialTerms />
	},
	{
		key: MERCHANTS_TABS.CONFIGURATIONS,
		pathname: MERCHANTS_TABS.CONFIGURATIONS,
		component: <MerchantConfigurationPage />
	},
	{
		key: MERCHANTS_TABS.FEES_HISTORY,
		pathname: MERCHANTS_TABS.FEES_HISTORY,
		component: <MerchantFeesHistoryPage />
	},
	// {
	// 	key: MERCHANTS_TABS.PAYMENT_GATEWAY,
	// 	pathname: MERCHANTS_TABS.PAYMENT_GATEWAY,
	// 	component: <PaymentGateway />
	// },
	{
		key: MERCHANTS_TABS.WEBHOOKS,
		pathname: MERCHANTS_TABS.WEBHOOKS,
		component: <MerchantWebhooksListPage />
	},
	{
		key: MERCHANTS_TABS.API_KEY,
		pathname: MERCHANTS_TABS.API_KEY,
		component: <ApiKey />
	},
	{
		key: MERCHANTS_TABS.TOKENIZATION,
		pathname: MERCHANTS_TABS.TOKENIZATION,
		component: <TokenizationPage />
	},

];

export const getFormattedSectionFormFields = (formikValues, name, data: IMerchantData, isAdmin: boolean): Params => {
	switch (name) {
		case MERCHANTS_TABS.BUSINESS:
			return getFormattedBusinessFormFields(formikValues);
		case MERCHANTS_TABS.ADDRESS:
			return getFormattedAddressFormFields(formikValues);
		case MERCHANTS_TABS.CONTACT:
			return getFormattedContactFormFields(formikValues);
		case MERCHANTS_TABS.DEVELOPER:
			return getFormattedDeveloperFormFields(formikValues);
		case MERCHANTS_TABS.CUSTOMER:
			return getFormattedCustomerFormFields(formikValues);
		case MERCHANTS_TABS.BANK:
			return getFormattedBankFormFields(formikValues, data);
		case MERCHANTS_TABS.CONFIGURATIONS:
			return getFormattedConfigurations(formikValues);
		case MERCHANTS_TABS.GENERAL:
			return isAdmin ? getAdminFormattedGeneralFormFields(formikValues) : getPartnerFormattedGeneralFormFields(formikValues);
		case MERCHANTS_TABS.SPECIAL_TERMS:
			return getFormattedSpecialTermsField(formikValues);
	}
};


export const getFormattedInitialData = (data, name, isAdmin): Params => {
	switch (name) {
		case MERCHANTS_TABS.BUSINESS:
			return getFormattedBusinessData(data);
		case MERCHANTS_TABS.ADDRESS:
			return getFormattedAddressData(data);
		case MERCHANTS_TABS.CONTACT:
			return getFormattedContactData(data);
		case MERCHANTS_TABS.DEVELOPER:
			return getFormattedDeveloperData(data);
		case MERCHANTS_TABS.CUSTOMER:
			return getFormattedCustomerData(data);
		case MERCHANTS_TABS.BANK:
			return getFormattedBankData(data);
		case MERCHANTS_TABS.CONFIGURATIONS:
			return getFormattedConfigurationData(data);
		case MERCHANTS_TABS.GENERAL:
			return isAdmin ? getAdminFormattedGeneralData(data) : getPartnerFormattedGeneralData(data);
		case MERCHANTS_TABS.SPECIAL_TERMS:
			return getFormattedSpecialTermsData(data);
	}
};

export const getInitialFormikData = (name, isAdmin: boolean): InitialValuesTypes => {
	switch (name) {
		case MERCHANTS_TABS.BUSINESS:
			return initialBusinessValues;
		case MERCHANTS_TABS.ADDRESS:
			return addressInitialValues;
		case MERCHANTS_TABS.CONTACT:
			return initialContactValues;
		case MERCHANTS_TABS.DEVELOPER:
			return initialDeveloperValues;
		case MERCHANTS_TABS.CUSTOMER:
			return customerInitialValues;
		case MERCHANTS_TABS.BANK:
			return bankInitialValues;
		case MERCHANTS_TABS.CONFIGURATIONS:
			return initialConfigurations;
		case MERCHANTS_TABS.GENERAL:
			return isAdmin ? initialAdminGeneralValues : initialPartnerGeneralValues;
		case MERCHANTS_TABS.SPECIAL_TERMS:
			return initialSpecialTermsValues;
	}
};

export const getFormikValidationSchema = (name, t): Yup.SchemaOf<InitialValuesTypes> => {
	switch (name) {
		case MERCHANTS_TABS.BUSINESS:
			return getBusinessValidationSchema(t);
		case MERCHANTS_TABS.CONTACT:
			return getContactValidationSchema(t);
		case MERCHANTS_TABS.DEVELOPER:
			return getDeveloperValidationSchema(t);
		case MERCHANTS_TABS.CUSTOMER:
			return getCustomerValidationSchema(t);
		case MERCHANTS_TABS.ADDRESS:
		case MERCHANTS_TABS.BANK:
		case MERCHANTS_TABS.CONFIGURATIONS:
		case MERCHANTS_TABS.GENERAL:
		case MERCHANTS_TABS.SPECIAL_TERMS:
		default: return null;
	}
};

export const getFormattedInitialFormikData = (name, data, isAdmin): InitialValuesTypes => {
	switch (name) {
		case MERCHANTS_TABS.BUSINESS:
			return convertToBusinessInitial(data);
		case MERCHANTS_TABS.ADDRESS:
			return convertAddressFieldsToInitial(data);
		case MERCHANTS_TABS.CONTACT:
			return convertToContactInitial(data);
		case MERCHANTS_TABS.DEVELOPER:
			return convertToDeveloperInitial(data);
		case MERCHANTS_TABS.CUSTOMER:
			return convertCustomerFieldsToInitial(data);
		case MERCHANTS_TABS.BANK:
			return convertBankFieldsToInitial(data);
		case MERCHANTS_TABS.CONFIGURATIONS:
			return convertConfigurationsToInitial(data);
		case MERCHANTS_TABS.GENERAL:
			return isAdmin ? convertAdminToGeneralInitialValues(data) : convertPartnerToGeneralInitialValues(data);
		case MERCHANTS_TABS.SPECIAL_TERMS:
			return convertToSpecialTermsInitial(data);
	}
};
